'use client';

import Image from 'next/image';
import { useState } from 'react';

const slides = [
  {
    image: '/images/gallery-1.webp',
    title: 'Elegance',
    subtitle: 'Timeless moments',
    description: 'Every detail tells a story',
  },
  {
    image: '/images/gallery-4.webp',
    title: 'Atmosphere',
    subtitle: 'Captured beauty',
    description: 'Minimalism, light and emotion',
  },
  {
    image: '/images/gallery-5.webp',
    title: 'Visual Stories',
    subtitle: 'Editorial collection',
    description: 'Frames inspired by aesthetics',
  },
  {
    image: '/images/gallery-12.webp',
    title: 'Refined Mood',
    subtitle: 'Modern elegance',
    description: 'A balance of art and simplicity',
  },
];

export default function GallerySlider() {
  const [current, setCurrent] = useState(0);

  const prev = () => {
    setCurrent((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const next = () => {
    setCurrent((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const getPosition = (index: number) => {
    if (index === current) return 'current';

    const nextIndex = current === slides.length - 1 ? 0 : current + 1;
    const prevIndex = current === 0 ? slides.length - 1 : current - 1;

    if (index === nextIndex) return 'next';
    if (index === prevIndex) return 'prev';

    return 'hidden';
  };

  return (
    <section data-component-id="src/components/ui/GallerySlider.tsx:57:4" data-component-path="src/components/ui/GallerySlider.tsx" data-component-line="57" data-component-end-line="144" data-component-file="GallerySlider.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22relative%20flex%20min-h-screen%20items-center%20justify-center%20overflow-hidden%20bg-white%20px-4%20py-20%20text-white%22%7D" className="relative flex min-h-screen items-center justify-center overflow-hidden bg-white px-4 py-20 text-white">
      {/* BLURRED BACKGROUND */}
      <div data-component-id="src/components/ui/GallerySlider.tsx:59:6" data-component-path="src/components/ui/GallerySlider.tsx" data-component-line="59" data-component-end-line="68" data-component-file="GallerySlider.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20z-0%22%7D" className="absolute inset-0 z-0">
        <Image data-component-id="src/components/ui/GallerySlider.tsx:60:8" data-component-path="src/components/ui/GallerySlider.tsx" data-component-line="60" data-component-end-line="66" data-component-file="GallerySlider.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22alt%22%3A%22%22%2C%22className%22%3A%22scale-110%20object-cover%20opacity-40%20blur-md%20transition-all%20duration-700%22%7D"
          src={slides[current].image}
          alt=""
          fill
          priority
          className="scale-110 object-cover opacity-40 blur-md transition-all duration-700"
        />
        <div data-component-id="src/components/ui/GallerySlider.tsx:67:8" data-component-path="src/components/ui/GallerySlider.tsx" data-component-line="67" data-component-end-line="67" data-component-file="GallerySlider.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-black%2F65%22%7D" className="absolute inset-0 bg-black/65" />
      </div>

      {/* PREV */}
      <button data-component-id="src/components/ui/GallerySlider.tsx:71:6" data-component-path="src/components/ui/GallerySlider.tsx" data-component-line="71" data-component-end-line="77" data-component-file="GallerySlider.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20left-4%20z-30%20text-6xl%20text-white%2F70%20transition%20hover%3Atext-white%20md%3Aleft-10%22%2C%22textContent%22%3A%22%E2%80%B9%22%7D"
        onClick={prev}
        className="absolute left-4 z-30 text-6xl text-white/70 transition hover:text-white md:left-10"
        aria-label="Previous slide"
      >
        ‹
      </button>

      {/* SLIDES */}
      <div data-component-id="src/components/ui/GallerySlider.tsx:80:6" data-component-path="src/components/ui/GallerySlider.tsx" data-component-line="80" data-component-end-line="134" data-component-file="GallerySlider.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20z-10%20flex%20h-%5B560px%5D%20w-full%20max-w-5xl%20items-center%20justify-center%22%7D" className="relative z-10 flex h-[560px] w-full max-w-5xl items-center justify-center">
        {slides.map((slide, index) => {
          const position = getPosition(index);

          return (
            <div data-component-id="src/components/ui/GallerySlider.tsx:85:12" data-component-path="src/components/ui/GallerySlider.tsx" data-component-line="85" data-component-end-line="131" data-component-file="GallerySlider.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              key={slide.image}
              className={`
                absolute aspect-[2/3] w-[230px] transition-all duration-700 ease-in-out md:w-[300px]

                ${position === 'current' ? 'z-20 translate-x-0 scale-110 opacity-100' : ''}

                ${
                  position === 'next'
                    ? 'z-10 translate-x-[80%] scale-95 opacity-70 [transform:translateX(110%)_rotateY(-52deg)]'
                    : ''
                }

                ${
                  position === 'prev'
                    ? 'z-10 -translate-x-[80%] scale-95 opacity-70 [transform:translateX(-110%)_rotateY(52deg)]'
                    : ''
                }

                ${position === 'hidden' ? 'pointer-events-none scale-75 opacity-0' : ''}
              `}
            >
              <div data-component-id="src/components/ui/GallerySlider.tsx:107:14" data-component-path="src/components/ui/GallerySlider.tsx" data-component-line="107" data-component-end-line="114" data-component-file="GallerySlider.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20h-full%20w-full%20overflow-hidden%22%7D" className="relative h-full w-full overflow-hidden">
                <Image data-component-id="src/components/ui/GallerySlider.tsx:108:16" data-component-path="src/components/ui/GallerySlider.tsx" data-component-line="108" data-component-end-line="113" data-component-file="GallerySlider.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22object-cover%20brightness-75%22%7D"
                  src={slide.image}
                  alt={slide.title}
                  fill
                  className="object-cover brightness-75"
                />
              </div>

              {position === 'current' && (
                <div data-component-id="src/components/ui/GallerySlider.tsx:117:16" data-component-path="src/components/ui/GallerySlider.tsx" data-component-line="117" data-component-end-line="129" data-component-file="GallerySlider.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-14%20left-%5B-45px%5D%20z-30%20max-w-%5B360px%5D%22%7D" className="absolute bottom-14 left-[-45px] z-30 max-w-[360px]">
                  <p data-component-id="src/components/ui/GallerySlider.tsx:118:18" data-component-path="src/components/ui/GallerySlider.tsx" data-component-line="118" data-component-end-line="120" data-component-file="GallerySlider.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20uppercase%20tracking-%5B0.35em%5D%20text-white%2F70%22%7D" className="text-sm uppercase tracking-[0.35em] text-white/70">
                    {slide.subtitle}
                  </p>

                  <h2 data-component-id="src/components/ui/GallerySlider.tsx:122:18" data-component-path="src/components/ui/GallerySlider.tsx" data-component-line="122" data-component-end-line="124" data-component-file="GallerySlider.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22mt-2%20font-cormorant%20text-%5B48px%5D%20italic%20leading-none%20md%3Atext-%5B70px%5D%22%7D" className="mt-2 font-cormorant text-[48px] italic leading-none md:text-[70px]">
                    {slide.title}
                  </h2>

                  <p data-component-id="src/components/ui/GallerySlider.tsx:126:18" data-component-path="src/components/ui/GallerySlider.tsx" data-component-line="126" data-component-end-line="128" data-component-file="GallerySlider.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-4%20text-sm%20font-light%20tracking-wide%20text-white%2F80%22%7D" className="mt-4 text-sm font-light tracking-wide text-white/80">
                    {slide.description}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* NEXT */}
      <button data-component-id="src/components/ui/GallerySlider.tsx:137:6" data-component-path="src/components/ui/GallerySlider.tsx" data-component-line="137" data-component-end-line="143" data-component-file="GallerySlider.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20right-4%20z-30%20text-6xl%20text-white%2F70%20transition%20hover%3Atext-white%20md%3Aright-10%22%2C%22textContent%22%3A%22%E2%80%BA%22%7D"
        onClick={next}
        className="absolute right-4 z-30 text-6xl text-white/70 transition hover:text-white md:right-10"
        aria-label="Next slide"
      >
        ›
      </button>
    </section>
  );
}
